import React from 'react'
import { t } from 'ttag'
import cx from 'classnames'
import { Icon, ChevronDownIcon } from 'components/shared/Icon/Icon'
import { Modal } from 'components/shared/Modal/Modal'
import { TRAITS } from 'components/common/ScoreCard/ScoreCard'
import {
  languages,
  translateTrait,
} from 'components/common/ScoreCard/descriptions'
import { buildContributorsForArea } from './contributionSignificance'
import { getAreaDetailSections, INITIAL_VISIBLE_FACETS } from './constants'
import styles from './TalentAreaDetailModal.module.scss'

const formatBadgeLabel = label =>
  label ? label.charAt(0).toUpperCase() + label.slice(1).toLowerCase() : label

const competencyParentTrait = Object.entries(TRAITS).reduce(
  (acc, [parent, codes]) => {
    codes.forEach(code => {
      acc[code] = parent
    })
    return acc
  },
  {},
)

const recruiterTraitLabel = (traitKey, language) =>
  languages[language]?.recruiter?.traits?.[traitKey]?.label ?? traitKey

const levelToScore = level => {
  if (level === 'high score') return 0.85
  if (level === 'low score') return 0.15
  return 0.5
}

const levelToBadge = level => {
  if (level === 'high score') {
    return { label: formatBadgeLabel(t`High`), tone: 'high' }
  }
  if (level === 'low score') {
    return { label: formatBadgeLabel(t`Low`), tone: 'low' }
  }
  return { label: formatBadgeLabel(t`Moderate`), tone: 'moderate' }
}

const getFacetAbstract = (code, direction, language) => {
  const { abstract = [] } = translateTrait(
    code,
    direction === 'high score is good' ? 0.85 : 0.15,
    language,
    false,
  )

  return Array.isArray(abstract) ? abstract[0] : ''
}

const getImportanceLabel = direction =>
  direction === 'high score is good'
    ? t`Here it is important to score high`
    : t`Here it is important to score low`

const FacetRow = ({ contributor, language, sectionTone }) => {
  const [isOpen, setIsOpen] = React.useState(false)
  const { facet_code: code, direction, level } = contributor
  const parentKey = competencyParentTrait[code]
  const { label } = translateTrait(code, levelToScore(level), language, false)
  const badge = levelToBadge(level)
  const description = getFacetAbstract(code, direction, language)
  const importanceLabel = getImportanceLabel(direction)

  return (
    <div
      className={cx(
        styles.areaDetailFacetCard,
        styles[`areaDetailFacetCard${sectionTone}`],
        { [styles.areaDetailFacetCardOpen]: isOpen },
      )}
    >
      <button
        className={styles.areaDetailFacetButton}
        type="button"
        onClick={() => setIsOpen(open => !open)}
      >
        <span className={styles.areaDetailFacetHeader}>
          <span className={styles.areaDetailFacetAccent} />
          <span className={styles.areaDetailFacetHeaderText}>
            <span className={styles.areaDetailFacetTitle}>{label}</span>
            <span className={styles.areaDetailFacetSubtitle}>
              {recruiterTraitLabel(parentKey, language)}
            </span>
          </span>
        </span>
        <Icon
          className={cx(styles.areaDetailFacetChevron, {
            [styles.areaDetailFacetChevronOpen]: isOpen,
          })}
          icon={ChevronDownIcon}
          size={14}
        />
      </button>
      {isOpen ? (
        <div className={styles.areaDetailFacetExpanded}>
          {description ? (
            <p className={styles.areaDetailFacetDescription}>{description}</p>
          ) : null}
          <p className={styles.areaDetailFacetImportance}>{importanceLabel}</p>
          <div className={styles.areaDetailFacetScoreRow}>
            <span className={styles.areaDetailFacetScoreLabel}>
              {t`The candidate scores:`}
            </span>
            <span
              className={cx(
                styles.areaDetailFacetBadge,
                styles[`areaDetailFacetBadge${badge.tone}`],
              )}
            >
              {badge.label}
            </span>
          </div>
        </div>
      ) : null}
    </div>
  )
}

const FacetListSection = ({
  contributors,
  hint,
  language,
  sectionTone,
  title,
}) => {
  const [visibleCount, setVisibleCount] = React.useState(INITIAL_VISIBLE_FACETS)

  React.useEffect(() => {
    setVisibleCount(INITIAL_VISIBLE_FACETS)
  }, [contributors])

  if (!contributors.length) {
    return null
  }

  const visibleContributors = contributors.slice(0, visibleCount)
  const totalFacetCount = contributors.length
  const hiddenCount = totalFacetCount - visibleCount
  const canShowMore = hiddenCount > 0
  const canShowFewer = visibleCount > INITIAL_VISIBLE_FACETS

  return (
    <section
      className={cx(
        styles.areaDetailSection,
        styles[`areaDetailSection${sectionTone}`],
      )}
    >
      <h3 className={styles.areaDetailSectionTitle}>{title}</h3>
      {hint ? <p className={styles.areaDetailSectionHint}>{hint}</p> : null}
      <div className={styles.areaDetailFacetList}>
        {visibleContributors.map(contributor => (
          <FacetRow
            key={contributor.facet_code}
            contributor={contributor}
            language={language}
            sectionTone={sectionTone}
          />
        ))}
        {canShowMore || canShowFewer ? (
          <button
            className={styles.areaDetailFacetListToggle}
            type="button"
            onClick={() => {
              if (canShowMore) {
                setVisibleCount(contributors.length)
                return
              }
              setVisibleCount(INITIAL_VISIBLE_FACETS)
            }}
          >
            {canShowMore
              ? t`Show all ${totalFacetCount} facets`
              : t`Show fewer`}
          </button>
        ) : null}
      </div>
    </section>
  )
}

export const TalentAreaDetailModal = ({
  areaRow,
  contributionSignificance,
  language,
  onClose,
}) => {
  const isOpen = areaRow !== null
  const areaKey = areaRow?.code
  const areaName = areaRow?.area ?? ''
  const contributors = React.useMemo(
    () => buildContributorsForArea(areaKey, contributionSignificance),
    [areaKey, contributionSignificance],
  )
  const resolvedLanguage = language || 'en'
  const sections = getAreaDetailSections()

  return (
    <Modal
      nestedModalEscapeCapture
      className={styles.areaDetailModal}
      cancelLabel={t`Close`}
      isOpen={isOpen}
      portalClassName={styles.areaDetailPortal}
      title={areaName}
      onCancel={onClose}
      onClose={onClose}
    >
      <Modal.Header className={styles.areaDetailHeader} />
      <Modal.Content className={styles.areaDetailContent}>
        <div className={styles.areaDetailSections}>
          {sections.map(section => (
            <FacetListSection
              key={section.contributorsKey}
              contributors={
                Array.isArray(contributors?.[section.contributorsKey])
                  ? contributors[section.contributorsKey]
                  : []
              }
              hint={section.hint}
              language={resolvedLanguage}
              sectionTone={section.sectionTone}
              title={section.title}
            />
          ))}
        </div>
      </Modal.Content>
    </Modal>
  )
}
